<script setup>
import { ref, watch, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import mabyApi from 'dashboard/api/mabyApi';

const props = defineProps({
  conversationId: { type: Number, required: true },
});

const { t } = useI18n();

const question = ref('');
const sentQuestion = ref('');
const isLoading = ref(false);
const response = ref(null);
const errorMessage = ref('');
const downloaded = ref(false);

// true quando uma pergunta foi enviada (exibe modo conversa)
const hasSent = computed(() => sentQuestion.value !== '');

watch(
  () => props.conversationId,
  () => {
    question.value = '';
    sentQuestion.value = '';
    response.value = null;
    errorMessage.value = '';
    downloaded.value = false;
  }
);

const friendlyError = (status, serverError) => {
  if (serverError === 'not_configured' || status === 503)
    return t('CONVERSATION.REPLYBOX.MABY_NOT_CONFIGURED');
  const map = {
    400: t('CONVERSATION.REPLYBOX.MABY_ERROR_400'),
    401: t('CONVERSATION.REPLYBOX.MABY_ERROR_401'),
    403: t('CONVERSATION.REPLYBOX.MABY_ERROR_403'),
    404: t('CONVERSATION.REPLYBOX.MABY_ERROR_404'),
    422: t('CONVERSATION.REPLYBOX.MABY_ERROR_400'),
    502: t('CONVERSATION.REPLYBOX.MABY_ERROR_DEFAULT'),
    504: t('CONVERSATION.REPLYBOX.MABY_ERROR_DEFAULT'),
  };
  return map[status] || t('CONVERSATION.REPLYBOX.MABY_ERROR_DEFAULT');
};

const reset = () => {
  sentQuestion.value = '';
  response.value = null;
  errorMessage.value = '';
  downloaded.value = false;
};

const sendQuestion = async () => {
  if (!question.value.trim() || isLoading.value) return;
  sentQuestion.value = question.value.trim();
  question.value = '';
  isLoading.value = true;
  response.value = null;
  errorMessage.value = '';
  downloaded.value = false;
  try {
    const { data } = await mabyApi.ask(
      props.conversationId,
      sentQuestion.value
    );
    response.value = data;
  } catch (err) {
    errorMessage.value = friendlyError(
      err?.response?.status,
      err?.response?.data?.error
    );
  } finally {
    isLoading.value = false;
  }
};

const handleDownload = () => {
  downloaded.value = true;
  setTimeout(reset, 300);
};

const handleKeydown = e => {
  if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') sendQuestion();
};
</script>

<template>
  <div class="maby-panel flex flex-col gap-2 p-3">
    <div class="maby-box rounded-lg border border-n-weak bg-n-background">
      <textarea
        v-if="!hasSent"
        v-model="question"
        rows="4"
        class="block w-full resize-none bg-transparent text-sm text-n-slate-12 placeholder:text-n-slate-9 p-2 focus:outline-none"
        :placeholder="$t('CONVERSATION.REPLYBOX.MABY_PLACEHOLDER')"
        @keydown="handleKeydown"
      />
      <div v-if="hasSent" class="flex flex-col gap-2 p-2 min-h-[96px]">
        <p class="text-sm text-n-slate-12 whitespace-pre-wrap">
          {{ sentQuestion }}
        </p>
        <div class="border-t border-n-weak" />
        <div v-if="isLoading" class="flex items-center gap-1 px-1 py-1">
          <span class="maby-dot maby-dot--1" />
          <span class="maby-dot maby-dot--2" />
          <span class="maby-dot maby-dot--3" />
        </div>
        <p v-else-if="errorMessage" class="text-sm text-n-ruby-11">
          {{ errorMessage }}
        </p>
        <p
          v-else-if="response && response.type === 'text'"
          class="text-sm text-n-slate-12 whitespace-pre-wrap"
        >
          {{ response.content }}
        </p>
        <div
          v-else-if="response && response.type === 'image' && !downloaded"
          class="flex flex-col gap-2"
        >
          <img
            :src="response.url"
            :alt="response.filename || 'Image'"
            class="max-w-full rounded-md"
          />
          <a
            :href="response.url"
            :download="response.filename || 'image'"
            class="inline-flex items-center gap-1 text-sm text-n-brand hover:underline w-fit"
            @click="handleDownload"
          >
            <span class="i-lucide-download size-4" />
            {{ $t('CONVERSATION.REPLYBOX.MABY_DOWNLOAD') }}
          </a>
        </div>
        <div
          v-else-if="response && response.type === 'pdf' && !downloaded"
          class="flex items-center gap-2"
        >
          <span
            class="i-lucide-file-text size-5 text-n-slate-11 flex-shrink-0"
          />
          <span class="text-sm text-n-slate-12 truncate flex-1">{{
            response.filename || 'document.pdf'
          }}</span>
          <a
            :href="response.url"
            :download="response.filename || 'document.pdf'"
            class="inline-flex items-center gap-1 text-sm text-n-brand hover:underline flex-shrink-0"
            @click="handleDownload"
          >
            <span class="i-lucide-download size-4" />
            {{ $t('CONVERSATION.REPLYBOX.MABY_DOWNLOAD') }}
          </a>
        </div>
        <button
          v-if="!isLoading"
          class="self-start text-n-slate-9 hover:text-n-brand transition-colors mt-auto pt-2"
          :title="$t('CONVERSATION.REPLYBOX.MABY_CLEAR')"
          @click="reset"
        >
          <span class="i-lucide-eraser size-4" />
        </button>
      </div>
    </div>

    <div class="flex justify-center">
      <button
        class="h-9 px-6 rounded-lg bg-n-brand text-white text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:enabled:bg-n-brand/90 transition-colors"
        :disabled="isLoading || (!hasSent && !question.trim())"
        @click="sendQuestion"
      >
        {{ $t('CONVERSATION.REPLYBOX.MABY_SEND') }}
      </button>
    </div>
  </div>
</template>

<style scoped>
.maby-box {
  min-height: 96px;
}

.maby-dot {
  @apply block size-2 rounded-full bg-n-slate-9;
  animation: maby-bounce 1.2s ease-in-out infinite;
}

.maby-dot--2 {
  animation-delay: 150ms;
}
.maby-dot--3 {
  animation-delay: 300ms;
}

@keyframes maby-bounce {
  0%,
  60%,
  100% {
    transform: translateY(0);
    opacity: 0.4;
  }
  30% {
    transform: translateY(-5px);
    opacity: 1;
  }
}
</style>
